"use client";

import * as React from "react";
import * as Collapsible from "@radix-ui/react-collapsible";
import * as Popover from "@radix-ui/react-popover";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  type LucideIcon,
} from "lucide-react";
import { cn } from "../../../lib/utils";
import { ScrollArea } from "../scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../tooltip";

// ============================================================================
// Context
// ============================================================================

interface SidebarContextValue {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  openGroupId: string | null;
  setOpenGroupId: (id: string | null) => void;
}

const SidebarContext = React.createContext<SidebarContextValue | undefined>(
  undefined,
);

function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a Sidebar");
  }
  return context;
}

// ============================================================================
// Sidebar
// ============================================================================

interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {
  collapsed?: boolean;
  defaultCollapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}

const Sidebar = React.forwardRef<HTMLDivElement, SidebarProps>(
  (
    {
      collapsed: controlledCollapsed,
      defaultCollapsed = false,
      onCollapsedChange,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const [uncontrolledCollapsed, setUncontrolledCollapsed] =
      React.useState(defaultCollapsed);
    const [openGroupId, setOpenGroupId] = React.useState<string | null>(null);

    const isControlled = controlledCollapsed !== undefined;
    const collapsed = isControlled
      ? controlledCollapsed
      : uncontrolledCollapsed;

    const setCollapsed = React.useCallback(
      (value: boolean) => {
        if (!isControlled) {
          setUncontrolledCollapsed(value);
        }
        onCollapsedChange?.(value);
      },
      [isControlled, onCollapsedChange],
    );

    return (
      <SidebarContext.Provider
        value={{ collapsed, setCollapsed, openGroupId, setOpenGroupId }}
      >
        <TooltipProvider delayDuration={0}>
          <aside
            ref={ref}
            data-collapsed={collapsed}
            className={cn(
              "relative flex h-full flex-col bg-background transition-all duration-200",
              collapsed ? "w-16" : "w-64",
              className,
            )}
            {...props}
          >
            {children}
          </aside>
        </TooltipProvider>
      </SidebarContext.Provider>
    );
  },
);
Sidebar.displayName = "Sidebar";

// ============================================================================
// SidebarHeader
// ============================================================================

const SidebarHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex h-14 items-center px-4", className)}
    {...props}
  />
));
SidebarHeader.displayName = "SidebarHeader";

// ============================================================================
// SidebarContent
// ============================================================================

const SidebarContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children }, ref) => (
  <div className="flex-1 overflow-hidden">
    <ScrollArea ref={ref} className={cn("h-full", className)}>
      <div className="flex flex-col py-2">{children}</div>
    </ScrollArea>
  </div>
));
SidebarContent.displayName = "SidebarContent";

// ============================================================================
// SidebarFooter
// ============================================================================

const SidebarFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-4", className)}
    {...props}
  />
));
SidebarFooter.displayName = "SidebarFooter";

// ============================================================================
// SidebarGroup
// ============================================================================

interface SidebarGroupContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  hasActiveChild: boolean;
  popoverOpen: boolean;
  setPopoverOpen: (open: boolean) => void;
  groupLabel: string;
}

const SidebarGroupContext = React.createContext<
  SidebarGroupContextValue | undefined
>(undefined);

// Context to track if item is nested inside group content and if parent has active child
interface SidebarNestedContextValue {
  isNested: boolean;
  parentHasActiveChild: boolean;
}
const SidebarNestedContext = React.createContext<SidebarNestedContextValue>({
  isNested: false,
  parentHasActiveChild: false,
});

// Counter to generate unique group IDs
let groupIdCounter = 0;

interface SidebarGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  defaultOpen?: boolean;
  hasActiveChild?: boolean;
  /**
   * Unique identifier for this group. If not provided, one will be generated.
   * Used to track which group is currently open (accordion behavior).
   */
  groupId?: string;
  /**
   * Label for the group, displayed in the collapsed popover header.
   */
  label?: string;
}

const SidebarGroup = React.forwardRef<HTMLDivElement, SidebarGroupProps>(
  (
    {
      hasActiveChild = false,
      groupId,
      label = "",
      className,
      children,
      ...props
    },
    ref,
  ) => {
    // Generate a stable ID for this group instance
    const stableIdRef = React.useRef<string>(
      groupId ?? `sidebar-group-${++groupIdCounter}`,
    );
    const id = stableIdRef.current;

    const { collapsed, openGroupId, setOpenGroupId } = useSidebar();
    const [popoverOpen, setPopoverOpen] = React.useState(false);

    // Open this group when it has an active child (e.g., on navigation)
    React.useEffect(() => {
      if (hasActiveChild) {
        setOpenGroupId(id);
      }
    }, [hasActiveChild, id, setOpenGroupId]);

    // This group is open if it matches the currently open group ID
    const open = openGroupId === id;

    const setOpen = React.useCallback(
      (shouldOpen: boolean) => {
        if (shouldOpen) {
          // Open this group (and close any other)
          setOpenGroupId(id);
        } else {
          // Close this group
          setOpenGroupId(null);
        }
      },
      [id, setOpenGroupId],
    );

    const contextValue = React.useMemo(
      () => ({
        open,
        setOpen,
        hasActiveChild,
        popoverOpen,
        setPopoverOpen,
        groupLabel: label,
      }),
      [open, setOpen, hasActiveChild, popoverOpen, label],
    );

    // When collapsed, wrap in a Popover for flyout menu
    if (collapsed) {
      return (
        <SidebarGroupContext.Provider value={contextValue}>
          <Popover.Root open={popoverOpen} onOpenChange={setPopoverOpen}>
            <div
              ref={ref}
              className={cn("flex flex-col", className)}
              {...props}
            >
              {children}
            </div>
          </Popover.Root>
        </SidebarGroupContext.Provider>
      );
    }

    return (
      <SidebarGroupContext.Provider value={contextValue}>
        <Collapsible.Root
          ref={ref}
          open={open}
          onOpenChange={setOpen}
          className={cn("flex flex-col", className)}
          {...props}
        >
          {children}
        </Collapsible.Root>
      </SidebarGroupContext.Provider>
    );
  },
);
SidebarGroup.displayName = "SidebarGroup";

// ============================================================================
// SidebarGroupLabel
// ============================================================================

interface SidebarGroupLabelProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "onClick"
> {
  icon?: LucideIcon;
  /**
   * Called when clicking the label text/icon area (navigates to overview).
   * The chevron area toggles collapse independently.
   */
  onClick?: () => void;
}

const SidebarGroupLabel = React.forwardRef<
  HTMLButtonElement,
  SidebarGroupLabelProps
>(({ icon: Icon, className, children, onClick, ...props }, ref) => {
  const { collapsed } = useSidebar();
  const groupContext = React.useContext(SidebarGroupContext);

  const handleMainClick = (e: React.MouseEvent) => {
    if (collapsed) {
      // In collapsed mode, clicking opens the popover (handled by Popover.Trigger)
      return;
    }
    if (onClick) {
      e.preventDefault();
      e.stopPropagation();
      // Open the group and navigate
      groupContext?.setOpen(true);
      onClick();
    }
  };

  const handleChevronClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Toggle the group open/close
    groupContext?.setOpen(!groupContext.open);
  };

  const content = (
    <div
      className={cn(
        "flex w-full items-center gap-4 px-4 py-2 text-sm font-medium transition-colors",
        groupContext?.hasActiveChild
          ? "bg-primary/25 text-foreground"
          : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
        collapsed && "justify-center px-0",
        className,
      )}
    >
      {collapsed ? (
        // When collapsed, render just the icon centered
        <button
          ref={ref}
          type="button"
          className="flex items-center justify-center cursor-pointer"
          onClick={handleMainClick}
          {...props}
        >
          {Icon && <Icon className="h-4 w-4 shrink-0" />}
        </button>
      ) : (
        // When expanded, show full label with chevron
        <>
          <button
            ref={ref}
            type="button"
            className="flex flex-1 items-center gap-4 text-left cursor-pointer"
            onClick={handleMainClick}
            {...props}
          >
            {Icon && <Icon className="h-4 w-4 shrink-0" />}
            <span className="flex-1">{children}</span>
          </button>
          {groupContext && (
            <button
              type="button"
              onClick={handleChevronClick}
              className="shrink-0 p-1 -mr-1 rounded hover:bg-muted/50 cursor-pointer"
            >
              <ChevronDown
                className={cn(
                  "h-4 w-4 transition-transform duration-200",
                  !groupContext.open && "-rotate-90",
                )}
              />
            </button>
          )}
        </>
      )}
    </div>
  );

  // When collapsed, use Popover.Trigger to open the flyout menu
  // Wrap with Tooltip to show label on hover (same as SidebarItem)
  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Popover.Trigger asChild>{content}</Popover.Trigger>
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={10}>
          {children}
        </TooltipContent>
      </Tooltip>
    );
  }

  return content;
});
SidebarGroupLabel.displayName = "SidebarGroupLabel";

// ============================================================================
// SidebarGroupContent
// ============================================================================

const SidebarGroupContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  const { collapsed } = useSidebar();
  const groupContext = React.useContext(SidebarGroupContext);

  // When collapsed, render as Popover.Content (flyout menu)
  if (collapsed) {
    return (
      <Popover.Portal>
        <Popover.Content
          ref={ref}
          side="right"
          sideOffset={0}
          align="start"
          className={cn(
            "z-50 min-w-45 rounded-lg bg-elevated p-1 text-text shadow-popover",
            "animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
            "data-[side=right]:slide-in-from-left-2",
            className,
          )}
          {...props}
        >
          {/* Group label header */}
          {groupContext?.groupLabel && (
            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground mb-1">
              {groupContext.groupLabel}
            </div>
          )}
          <SidebarNestedContext.Provider
            value={{
              isNested: true,
              parentHasActiveChild: groupContext?.hasActiveChild ?? false,
            }}
          >
            <div className="flex flex-col">{children}</div>
          </SidebarNestedContext.Provider>
        </Popover.Content>
      </Popover.Portal>
    );
  }

  return (
    <Collapsible.Content
      ref={ref}
      className={cn(
        "overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up",
        className,
      )}
      {...props}
    >
      <SidebarNestedContext.Provider
        value={{
          isNested: true,
          parentHasActiveChild: groupContext?.hasActiveChild ?? false,
        }}
      >
        <div className="flex flex-col">{children}</div>
      </SidebarNestedContext.Provider>
    </Collapsible.Content>
  );
});
SidebarGroupContent.displayName = "SidebarGroupContent";

// ============================================================================
// SidebarItem
// ============================================================================

interface SidebarItemProps extends React.HTMLAttributes<HTMLElement> {
  icon?: LucideIcon;
  href?: string;
  active?: boolean;
  disabled?: boolean;
  asChild?: boolean;
  badge?: string;
  comingSoon?: boolean;
}

const SidebarItem = React.forwardRef<HTMLElement, SidebarItemProps>(
  (
    {
      icon: Icon,
      href,
      active,
      disabled,
      badge,
      comingSoon,
      className,
      children,
      onClick,
      ...props
    },
    ref,
  ) => {
    const { collapsed } = useSidebar();
    const { isNested, parentHasActiveChild } =
      React.useContext(SidebarNestedContext);
    const groupContext = React.useContext(SidebarGroupContext);

    // If comingSoon, automatically disable the item
    const isDisabled = disabled || comingSoon;

    // Check if we're inside a collapsed group (popover is open)
    const isInPopover = collapsed && groupContext?.popoverOpen;

    // Handle click to close popover when navigating
    const handleClick = (e: React.MouseEvent) => {
      if (isInPopover && groupContext) {
        // Close the popover after a short delay to allow navigation
        setTimeout(() => groupContext.setPopoverOpen(false), 100);
      }
      // Call the original onClick if provided
      if (onClick) {
        (onClick as (e: React.MouseEvent) => void)(e);
      }
    };

    // In popover mode, always show full content (not just icon)
    const showFullContent = !collapsed || isInPopover;

    // Determine icon size - smaller for nested items
    const iconClasses = isNested
      ? "h-1.5 w-1.5 shrink-0 fill-current"
      : "h-4 w-4 shrink-0";

    const content = (
      <>
        {Icon && <Icon className={iconClasses} />}
        {showFullContent && (
          <>
            <span className="flex-1 truncate text-left">{children}</span>
            {comingSoon && (
              <span className="ml-auto shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
                Soon
              </span>
            )}
            {badge && !comingSoon && (
              <span className="ml-auto shrink-0 rounded bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary">
                {badge}
              </span>
            )}
          </>
        )}
      </>
    );

    const itemClasses = cn(
      "relative flex items-center gap-3 px-4 py-2 text-sm transition-all duration-200 cursor-pointer",
      collapsed && !isInPopover && "justify-center px-0",
      isNested && !collapsed && "pl-9",
      active
        ? "bg-primary text-primary-foreground"
        : parentHasActiveChild && isNested
          ? "bg-primary/25 text-foreground hover:bg-primary/35"
          : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
      isDisabled && "pointer-events-none opacity-50",
      className,
    );

    const item = href ? (
      <a
        ref={ref as React.Ref<HTMLAnchorElement>}
        href={isDisabled ? undefined : href}
        className={itemClasses}
        aria-disabled={isDisabled}
        onClick={handleClick}
        {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {content}
      </a>
    ) : (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        className={itemClasses}
        disabled={isDisabled}
        onClick={handleClick}
        {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        {content}
      </button>
    );

    // When collapsed but NOT in popover (standalone items), show tooltip
    if (collapsed && !isInPopover) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>{item}</TooltipTrigger>
          <TooltipContent side="right" sideOffset={10}>
            {children}
          </TooltipContent>
        </Tooltip>
      );
    }

    return item;
  },
);
SidebarItem.displayName = "SidebarItem";

// ============================================================================
// SidebarSeparator
// ============================================================================

const SidebarSeparator = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("my-2 h-px bg-muted", className)} {...props} />
));
SidebarSeparator.displayName = "SidebarSeparator";

// ============================================================================
// SidebarSection (Section Headers like "OVERVIEW", "FINANCIAL MANAGEMENT")
// ============================================================================

const SidebarSection = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  const { collapsed } = useSidebar();

  if (collapsed) {
    return <SidebarSeparator />;
  }

  return (
    <div
      ref={ref}
      className={cn(
        "px-4 py-2 mt-4 first:mt-0 text-xs font-semibold uppercase tracking-wider text-muted-foreground",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
});
SidebarSection.displayName = "SidebarSection";

// ============================================================================
// SidebarTrigger
// ============================================================================

interface SidebarTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  position?: "header" | "footer" | "floating";
}

const SidebarTrigger = React.forwardRef<HTMLButtonElement, SidebarTriggerProps>(
  ({ position = "footer", className, ...props }, ref) => {
    const { collapsed, setCollapsed } = useSidebar();

    const baseClasses = cn(
      "flex items-center justify-center rounded-md p-2 text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground",
      position === "floating" &&
        "absolute -right-3 top-6 z-10 h-6 w-6 rounded-full bg-panel shadow-raised",
      className,
    );

    return (
      <button
        ref={ref}
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        className={baseClasses}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        {...props}
      >
        {collapsed ? (
          <ChevronRight className="h-4 w-4" />
        ) : (
          <ChevronLeft className="h-4 w-4" />
        )}
      </button>
    );
  },
);
SidebarTrigger.displayName = "SidebarTrigger";

// ============================================================================
// Exports
// ============================================================================

export {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarSection,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarItem,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
};
